import { useState, useEffect } from "react";
import { Card } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { supabase } from "@/integrations/supabase/client";
import { useAuth } from "@/hooks/useAuth";
import { Loader2, Activity, TrendingUp, AlertCircle, CheckCircle2, Phone } from "lucide-react";
import { LineChart, Line, BarChart, Bar, PieChart, Pie, Cell, XAxis, YAxis, CartesianGrid, Tooltip, Legend, ResponsiveContainer } from "recharts";

interface MonitoringStats {
  avgConfidence: number;
  totalAnalyses: number;
  lowConfidenceCount: number;
  lowConfidencePercentage: number;
  errorBreakdown: { type: string; count: number }[];
  timeSeries: { date: string; count: number; avgConfidence: number }[];
  voiceHealth: {
    totalSessions: number;
    successRate: number;
    errorCount: number;
    lastError: string | { message: string; type: string; timestamp: string } | null;
  };
}

const COLORS = ["#8b5cf6", "#ec4899", "#f59e0b", "#10b981", "#3b82f6"];

export function AIInsightsTab() {
  const { user } = useAuth();
  const [loading, setLoading] = useState(true);
  const [days, setDays] = useState(7);
  const [stats, setStats] = useState<MonitoringStats | null>(null);

  useEffect(() => {
    loadStats();
  }, [user, days]);

  const loadStats = async () => {
    if (!user) return;

    try {
      const { data: profile } = await supabase
        .from("profiles")
        .select("org_id")
        .eq("id", user.id)
        .single();

      if (!profile?.org_id) return;

      const { data, error } = await supabase.functions.invoke("ai-monitoring-stats", {
        body: { days },
      });

      if (error) throw error;
      setStats(data);
    } catch (error) {
      console.error("Error loading AI monitoring stats:", error);
    } finally {
      setLoading(false);
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center py-12">
        <Loader2 className="w-8 h-8 animate-spin text-primary" />
      </div>
    );
  }

  if (!stats) {
    return (
      <div className="text-center py-12">
        <p className="text-muted-foreground">No data available</p>
      </div>
    );
  }

  const successRate = stats.totalAnalyses > 0 
    ? ((stats.totalAnalyses - stats.lowConfidenceCount) / stats.totalAnalyses * 100).toFixed(1)
    : "0";

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <div>
          <h2 className="text-2xl font-bold mb-2">AI Insights</h2>
          <p className="text-muted-foreground">Monitor AI performance and voice health</p>
        </div>
        <Select value={days.toString()} onValueChange={(v) => setDays(Number(v))}>
          <SelectTrigger className="w-32">
            <SelectValue />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="7">7 days</SelectItem>
            <SelectItem value="30">30 days</SelectItem>
            <SelectItem value="90">90 days</SelectItem>
          </SelectContent>
        </Select>
      </div>

      {/* Voice Genie Health */}
      <Card className="p-6 border-2 border-neon-purple/20">
        <div className="flex items-center gap-3 mb-4">
          <Phone className="w-5 h-5 text-neon-purple" />
          <h3 className="text-lg font-semibold">Voice Genie Health</h3>
          <Badge variant="outline" className="ml-auto bg-success/10 text-success">
            Active
          </Badge>
        </div>
        <div className="grid grid-cols-3 gap-4">
          <div>
            <p className="text-sm text-muted-foreground mb-1">Total Sessions</p>
            <p className="text-2xl font-bold">{stats.voiceHealth.totalSessions}</p>
          </div>
          <div>
            <p className="text-sm text-muted-foreground mb-1">Success Rate</p>
            <p className="text-2xl font-bold">{stats.voiceHealth.successRate?.toFixed(1) ?? '0'}%</p>
          </div>
          <div>
            <p className="text-sm text-muted-foreground mb-1">Errors</p>
            <p className="text-2xl font-bold text-destructive">{stats.voiceHealth.errorCount}</p>
          </div>
        </div>
        {stats.voiceHealth.lastError && (
          <div className="mt-4 p-3 rounded-lg bg-destructive/10">
            <p className="text-xs text-muted-foreground mb-1">Last Error:</p>
            <p className="text-sm">
              {typeof stats.voiceHealth.lastError === 'string' 
                ? stats.voiceHealth.lastError 
                : stats.voiceHealth.lastError.message || JSON.stringify(stats.voiceHealth.lastError)}
            </p>
          </div>
        )}
      </Card>

      {/* Key Metrics */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
        <Card className="p-6">
          <div className="flex items-start justify-between mb-3">
            <Activity className="w-6 h-6 text-primary" />
          </div>
          <p className="text-sm text-muted-foreground mb-1">Total Analyses</p>
          <p className="text-3xl font-bold">{stats.totalAnalyses}</p>
        </Card>

        <Card className="p-6">
          <div className="flex items-start justify-between mb-3">
            <TrendingUp className="w-6 h-6 text-success" />
          </div>
          <p className="text-sm text-muted-foreground mb-1">Avg Confidence</p>
          <p className="text-3xl font-bold">{stats.avgConfidence?.toFixed(1) ?? '0'}%</p>
        </Card>

        <Card className="p-6">
          <div className="flex items-start justify-between mb-3">
            <CheckCircle2 className="w-6 h-6 text-success" />
          </div>
          <p className="text-sm text-muted-foreground mb-1">Success Rate</p>
          <p className="text-3xl font-bold">{successRate}%</p>
        </Card>

        <Card className="p-6">
          <div className="flex items-start justify-between mb-3">
            <AlertCircle className="w-6 h-6 text-warning" />
          </div>
          <p className="text-sm text-muted-foreground mb-1">Low Confidence</p>
          <p className="text-3xl font-bold">{stats.lowConfidenceCount ?? 0}</p>
          <p className="text-xs text-muted-foreground mt-1">
            {stats.lowConfidencePercentage?.toFixed(1) ?? '0'}% of total
          </p>
        </Card>
      </div>

      {/* Charts */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {/* Confidence Trend */}
        <Card className="p-6">
          <h3 className="text-lg font-semibold mb-4">Confidence Score Trend</h3>
          <ResponsiveContainer width="100%" height={300}>
            <LineChart data={stats.timeSeries}>
              <CartesianGrid strokeDasharray="3 3" stroke="hsl(var(--border))" />
              <XAxis dataKey="date" stroke="hsl(var(--muted-foreground))" />
              <YAxis stroke="hsl(var(--muted-foreground))" />
              <Tooltip contentStyle={{ backgroundColor: "hsl(var(--card))", border: "1px solid hsl(var(--border))" }} />
              <Legend />
              <Line type="monotone" dataKey="avgConfidence" name="Avg Confidence" stroke="hsl(var(--primary))" strokeWidth={2} />
            </LineChart>
          </ResponsiveContainer>
        </Card>

        {/* Error Breakdown */}
        <Card className="p-6">
          <h3 className="text-lg font-semibold mb-4">Error Breakdown</h3>
          <ResponsiveContainer width="100%" height={300}>
            <PieChart>
              <Pie data={stats.errorBreakdown} dataKey="count" nameKey="type" cx="50%" cy="50%" outerRadius={100} label>
                {stats.errorBreakdown.map((entry, index) => (
                  <Cell key={`cell-${index}`} fill={COLORS[index % COLORS.length]} />
                ))}
              </Pie>
              <Tooltip contentStyle={{ backgroundColor: "hsl(var(--card))", border: "1px solid hsl(var(--border))" }} />
              <Legend />
            </PieChart>
          </ResponsiveContainer>
        </Card>

        {/* Daily Volume */}
        <Card className="p-6 lg:col-span-2">
          <h3 className="text-lg font-semibold mb-4">Daily Analysis Volume</h3>
          <ResponsiveContainer width="100%" height={300}>
            <BarChart data={stats.timeSeries}>
              <CartesianGrid strokeDasharray="3 3" stroke="hsl(var(--border))" />
              <XAxis dataKey="date" stroke="hsl(var(--muted-foreground))" />
              <YAxis stroke="hsl(var(--muted-foreground))" />
              <Tooltip contentStyle={{ backgroundColor: "hsl(var(--card))", border: "1px solid hsl(var(--border))" }} />
              <Legend />
              <Bar dataKey="count" name="Analyses" fill="hsl(var(--primary))" />
            </BarChart>
          </ResponsiveContainer>
        </Card>
      </div>
    </div>
  );
}
